import React, {useState} from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import {useNavigate} from 'react-router';
import {useSession} from "../../../../firebase/UserProvider";
import AddIcon from "@mui/icons-material/Add";

const _awpBase = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';
function _authH() { const t = localStorage.getItem('access_token'); return t ? {'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json'} : {'Content-Type': 'application/json'}; }

// Small header component for the Feeds list.
// Props:
// - onCreate: optional function called when a new feed is created. Receives the feed object.
const HeaderOfListOfFeeds = ({onFeedCreated}) => {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [requestText, setRequestText] = useState('');
    const [error, setError] = useState(null);

    const {user} = useSession() || {};
    const navigate = useNavigate();

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        if (submitting) return;
        setOpen(false);
        setError(null);
    };

    const buildFeedDraft = (rawText) => {
        const text = rawText.trim();
        const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
        const firstLine = lines[0] || text;
        const title = firstLine.length > 120 ? `${firstLine.slice(0, 117)}...` : firstLine;

        return {
            title,
            description: text,
            // Keep existing feed shape so downstream UI continues to work.
            sources: [],
            entities: [],
            indicators: [],
            triggers: [],
        };
    };

    const handleSubmit = async () => {
        if (!requestText.trim()) {
            setError('Please describe what you need');
            return;
        }

        const payload = {
            ...buildFeedDraft(requestText),
            createdAt: new Date().toISOString(),
            createdBy: user ? {uid: user.uid, displayName: user.displayName, email: user.email} : null,
            owner: user ? user.uid : null,
        };

        setSubmitting(true);
        setError(null);

        try {
            const feedResp = await fetch(`${_awpBase}/api/feeds`, {method: 'POST', headers: _authH(), body: JSON.stringify(payload)});
            const ref = feedResp.ok ? await feedResp.json() : null;
            if (ref && ref.id) {
                const createIirPayload = {docId: ref.id, status: 'scheduled'};
                // Store request for audit.
                await fetch(`${_awpBase}/api/documents/create_iir_requests/${ref.id}`, {
                    method: 'PUT', headers: _authH(),
                    body: JSON.stringify({feedId: ref.id, payload: createIirPayload, createdAt: new Date().toISOString(), createdBy: user ? {uid: user.uid, email: user.email || null} : null}),
                }).catch(() => {});

                // Close dialog, refresh the list, and switch to the new feed.
                setRequestText('');
                setOpen(false);
                if (typeof onFeedCreated === 'function') onFeedCreated();
                navigate(`/feed/${ref.id}`);
            } else {
                setError('Failed to create feed — no ID returned');
            }
        } catch (err) {
            console.error('Error creating feed:', err);
            setError(err.message || 'Failed to create feed');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Stack sx={{p: 2}}>
            <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon/>}
                onClick={handleOpen}
            >
                New Feed
            </Button>

            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>Create Feed</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{mt: 1}}>
                        <TextField
                            label="What do you need?"
                            autoFocus={true}
                            value={requestText}
                            onChange={(e) => setRequestText(e.target.value)}
                            fullWidth
                            required
                            multiline
                            minRows={6}
                            helperText="Describe the feed in plain language"
                        />

                        {error && (
                            <Typography color="error" variant="body2">{error}</Typography>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
                        {submitting ? <CircularProgress size={20}/> : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default HeaderOfListOfFeeds;
